import type { SubscriptionState } from '@/hooks/useSubscriptionState';
import { STRINGS } from '@/strings/strings';

/**
 * deriveTierCardState — pure function mapping the (subscriptionState ×
 * cardTier × cardInterval) tuple to the CTA + badge variant rendered
 * on a Browse-Mode TierCard (Batch 11 Commit 3 — A-02 state-matrix
 * implementation per Briefing K-A-02 state-table).
 *
 * The 6 briefing-specified states:
 *
 *   1. trial            → "Abo abschließen"           on every card
 *   2. current          → "Dein aktueller Plan"       (disabled badge)
 *      same tier AND same interval as the active sub
 *   3. upgrade          → "Upgrade"                   card ranks above current
 *   4. downgrade        → "Downgrade"                 card ranks below current
 *   5. pending-cancel   → "Plan wählen und reaktivieren" on every card
 *   6. cancel-downgrade → "Downgrade rückgängig machen"  ONLY on the
 *      pendingDowngradeTier card with matching interval (the
 *      "target-undercut" case from the Briefing footnote)
 *
 * Priority order (top wins): pending-cancel > cancel-downgrade > trial >
 * current > upgrade/downgrade. Past-Due users hit the same upgrade/
 * downgrade/current path as active users — Briefing doesn't require
 * Browse-Mode CTAs to disable for Past-Due, and the Past-Due banner on
 * the Aktueller-Plan-Card is the action-required surface.
 *
 * Rank semantics (`compareCardToCurrent`):
 *   - Different tiers          → tier rank wins (entdecker < abenteurer
 *                                < geschichten_profi by monthlyCredits).
 *   - Same tier, same interval → 0 (current-plan badge).
 *   - Same tier, yearly card vs monthly current → +1 (effective-price
 *     upgrade per Briefing "Jährlich -20%").
 *   - Same tier, monthly card vs yearly current → -1 (effective-price
 *     downgrade).
 *
 * `action` field drives the click-handler branch in TierCard:
 *   - 'choose-plan'                  → navigate to /checkout/review?plan={planSlug}
 *                                      (first-time subscriber path — trial /
 *                                      no-active-sub. Routes through
 *                                      createConsentAndCheckout for the
 *                                      initial Stripe Checkout Session.)
 *   - 'subscription-update-deep-link' → POST /api/subscriptions/portal-session
 *                                      with flow='subscription_update' (Stripe
 *                                      Portal plan-update flow). Active-sub
 *                                      upgrade / downgrade / cycle-switch
 *                                      (Bug #4 follow-up, 2026-05-13) — Stripe
 *                                      Portal handles plan-change in-place
 *                                      with proration; webhook
 *                                      customer.subscription.updated reactively
 *                                      applies the change to the DB row.
 *                                      Prevents the parallel-subscription
 *                                      double-charge the prior 'choose-plan'
 *                                      wiring caused.
 *   - 'reactivate-deep-link'         → same portal-session deep-link
 *                                      (Stripe Portal plan-pick + cancel-undo)
 *   - 'cancel-downgrade-deep-link'   → same portal-session deep-link
 *                                      (one Portal flow handles both
 *                                      cases server-side)
 *   - 'no-op'                        → disabled, no click handler fires
 */
export type TierCardCtaAction =
  | 'choose-plan'
  | 'subscription-update-deep-link'
  // PWA-B (2026-05-21): 'reactivate-deep-link' + 'cancel-downgrade-deep-link'
  // are no longer produced (pending-state cards are now disabled / route the
  // discard in-app via 'cancel-downgrade-in-app'). Kept in the union this
  // commit for scope discipline — marked for removal in a later commit.
  | 'reactivate-deep-link'
  | 'cancel-downgrade-deep-link'
  | 'cancel-downgrade-in-app'
  | 'no-op';

export interface TierCardState {
  /** Visible CTA button label. Empty string when `disabled=true` (badge replaces the CTA). */
  ctaLabel: string;
  /** Visual variant for the CTA — 'primary' = gold, 'success' = green, 'disabled' = grey. */
  ctaVariant: 'primary' | 'success' | 'disabled';
  /** True for the current-plan card; CTA is replaced by `badgeLabel`. */
  disabled: boolean;
  /** Optional badge text (only set when `disabled=true`). */
  badgeLabel?: string;
  /** Click-handler discriminator — see JSDoc above. */
  action: TierCardCtaAction;
}

export type TierCardCardInterval = 'yearly' | 'monthly';

export interface DeriveTierCardStateInput {
  subscriptionState: SubscriptionState | null;
  cardTierKey: string;
  cardInterval: TierCardCardInterval;
}

const TIER_RANK: Record<string, number> = {
  entdecker: 1,
  abenteurer: 2,
  geschichten_profi: 3,
};

/**
 * Normalize the SubscriptionState's `billingInterval` ('monthly' |
 * 'annual' | null) to the BrowseInterval namespace ('yearly' |
 * 'monthly'). Backend uses 'annual' on the persisted column; the
 * PublicPlan catalog + Browse-Mode toggle use 'yearly'. Bridge here.
 */
function normalizeStateInterval(
  i: SubscriptionState['billingInterval'],
): TierCardCardInterval | null {
  if (i === 'monthly') return 'monthly';
  if (i === 'annual') return 'yearly';
  return null;
}

function compareCardToCurrent(
  cardTier: string,
  currentTier: string,
  cardInterval: TierCardCardInterval,
  currentInterval: TierCardCardInterval,
): -1 | 0 | 1 {
  // Fallback to 0 for unknown tiers — strict-mode `noUncheckedIndexedAccess`
  // surfaces these as `number | undefined`. An unknown tier ranking below
  // all known tiers means it consistently shows as a downgrade vs. any
  // known card, which is the right sentinel for the comparison contract.
  const cardRank = TIER_RANK[cardTier] ?? 0;
  const currentRank = TIER_RANK[currentTier] ?? 0;
  if (cardRank !== currentRank) {
    return cardRank > currentRank ? 1 : -1;
  }
  if (cardInterval === currentInterval) return 0;
  // Same tier, different interval — yearly is the upgrade direction
  // per Briefing "Jährlich -20%" effective-price framing.
  return cardInterval === 'yearly' ? 1 : -1;
}

export function deriveTierCardState({
  subscriptionState,
  cardTierKey,
  cardInterval,
}: DeriveTierCardStateInput): TierCardState {
  const browseS = STRINGS.account.subscription.browseMode;

  // 1. Trial / no-active-subscription — every card = "Abo abschließen".
  //    Status===null OR status==='trial' OR tierKey===null collapses
  //    to the trial branch. tierKey===null is the strongest signal
  //    (DTO carries it for users with no active subscription row).
  if (
    !subscriptionState ||
    subscriptionState.status === 'trial' ||
    subscriptionState.tierKey === null
  ) {
    return {
      ctaLabel: browseS.cta.trial,
      ctaVariant: 'primary',
      disabled: false,
      action: 'choose-plan',
    };
  }

  // Pending-state cards (PWA-B 2026-05-21): compute the billing interval and
  // the current-plan identity once — both pending branches below need them.
  const currentInterval = normalizeStateInterval(subscriptionState.billingInterval);
  const isCurrentPlanCard =
    subscriptionState.tierKey === cardTierKey &&
    currentInterval !== null &&
    cardInterval === currentInterval;

  // 2. Pending-cancel (PWA-B) — the reactivate CTA lives exclusively on the
  //    SubscriptionSummaryCard banner. Tier cards are non-actionable: the
  //    current plan shows the "Dein aktueller Plan" badge, every other card is
  //    disabled. The BrowseMode grid header explains the state. ('reactivate-
  //    deep-link' is no longer produced — see TierCardCtaAction note.)
  if (subscriptionState.cancelAtPeriodEnd) {
    if (isCurrentPlanCard) {
      return {
        ctaLabel: '',
        ctaVariant: 'disabled',
        disabled: true,
        badgeLabel: browseS.cta.currentPlanBadge,
        action: 'no-op',
      };
    }
    return {
      ctaLabel: '',
      ctaVariant: 'disabled',
      disabled: true,
      action: 'no-op',
    };
  }

  // 3. Pending-downgrade (PWA-B) — only the downgrade-target card at the
  //    matching interval is actionable: "Downgrade rückgängig machen" routed to
  //    the in-app discard endpoint ('cancel-downgrade-in-app'), NOT the Stripe
  //    Portal. The current plan shows the badge; every other card is disabled
  //    until the user discards the pending change. Grid header explains.
  if (subscriptionState.pendingDowngradeTier !== null) {
    if (isCurrentPlanCard) {
      return {
        ctaLabel: '',
        ctaVariant: 'disabled',
        disabled: true,
        badgeLabel: browseS.cta.currentPlanBadge,
        action: 'no-op',
      };
    }
    if (
      subscriptionState.pendingDowngradeTier === cardTierKey &&
      currentInterval !== null &&
      cardInterval === currentInterval
    ) {
      return {
        ctaLabel: browseS.cta.cancelDowngrade,
        ctaVariant: 'success',
        disabled: false,
        action: 'cancel-downgrade-in-app',
      };
    }
    return {
      ctaLabel: '',
      ctaVariant: 'disabled',
      disabled: true,
      action: 'no-op',
    };
  }

  // 4. Active state — rank comparison.
  if (currentInterval === null || subscriptionState.tierKey === null) {
    // Defensive: should be unreachable given the trial-branch guard
    // above, but keeps the state-matrix total. Falls back to
    // "Plan wählen" placeholder so the user can still attempt
    // checkout.
    return {
      ctaLabel: browseS.cta.choosePlanPlaceholder,
      ctaVariant: 'primary',
      disabled: false,
      action: 'choose-plan',
    };
  }

  const cmp = compareCardToCurrent(
    cardTierKey,
    subscriptionState.tierKey,
    cardInterval,
    currentInterval,
  );

  if (cmp === 0) {
    return {
      ctaLabel: '',
      ctaVariant: 'disabled',
      disabled: true,
      badgeLabel: browseS.cta.currentPlanBadge,
      action: 'no-op',
    };
  }
  // Bug #4 follow-up (2026-05-13): active-sub upgrade + downgrade
  // (including same-tier cycle-switch Monthly↔Yearly) route through
  // the Stripe Customer Portal subscription_update flow, NOT through
  // /checkout/review. The prior 'choose-plan' wiring would call
  // createConsentAndCheckout → Stripe Checkout Session → SECOND
  // parallel subscription (double-charge). Portal handles plan
  // change in-place with proration; webhook
  // customer.subscription.updated (Phase 3D.3, sub-cases A/F/G)
  // reactively applies the change to the existing DB row.
  if (cmp > 0) {
    return {
      ctaLabel: browseS.cta.upgrade,
      ctaVariant: 'primary',
      disabled: false,
      action: 'subscription-update-deep-link',
    };
  }
  return {
    ctaLabel: browseS.cta.downgrade,
    ctaVariant: 'primary',
    disabled: false,
    action: 'subscription-update-deep-link',
  };
}
